import { batch } from "react-redux"
import { ActionResult, Dispatch } from "../../../utility/common/storeHelper"
import * as constants from "../constants"
import { RootState } from "../../rootReducer"
import { getArticlePermittedAction, hasCatalogAccess } from "../../../utility/knowledgeBase/knowledgeBase"
import {
    buildArticleActionUrl,
    buildSharedArticleActionUrl,
    normalizeArticleSharedUrl
} from "../../../utility/knowledgeBase/articleUrlHelper"
import knowledgeBaseController from "../../../api/controllers/knowledgeBase"
import {
    actions,
    clearCurrentArticle,
    clearCurrentBranch,
    CurrentArticleState,
    getArticleProcess,
    getUnmarkedMessagesFailed,
    getUnmarkedMessagesProcess,
    getUnmarkedMessagesSuccess,
    mutateCurrentBranch,
    setArticleLockStatus,
    updateArticleFavoriteBlockTree,
    updateArticleProcess,
    updateUnmarkedMessageSuccess
} from "../slice"
import { handleHttpException, handleHttpExceptionWithoutAction } from "../../handleHttpException"
import { push } from "connected-react-router"
import {
    CategoryAddedEvent,
    CategoryMutation,
    CategoryTreeData,
    CreateCategoryRequest,
    GetRelatedCategoriesRequest,
    MoveCategoryRequest,
    RemoveCategoryFromCatalogEvent
} from "../../../models/category"
import {
    Article,
    ArticleAction,
    ArticleEditFormValues,
    ArticleQuestion,
    ArticleQuestionType,
    ArticleStatusType,
    EArticleEditFormChangesInheritance,
    GetUnmarkedMessageRequest,
    MarkedMessage,
    MarkedMessageHash,
    PublishArticleRequest,
    QuestionType,
    SearchArticlesRequest,
    UpdateActualArticlePermissionsRequest,
    UpdateArticleRequest,
    UpdateScenarioRequest
} from "../../../models/article"
import {
    FavoriteArticleInfo,
    FavoriteArticlesBlock,
    FavoriteArticlesBlockExpandRequest,
    FavoriteArticlesBlockMoveRequest,
    FavoriteArticlesBlockRequest
} from "../../../models/articleFavoriteBlock"
import { Catalog, CatalogFormValues, CreateCatalogRequest, UpdateCatalogRequest } from "../../../models/catalog"
import { KnowledgeBasePermission } from "../../../models/knowledgeBase/permission"
import { Scenario } from "../../../models/scenario"
import notificationController from "../../../api/controllers/notification"
import { usersController } from "../../../api/controllers/users"
import { apiCallExceptionToSystemError } from "../../../utility/common/apiCallExceptionToSystemError"
import {
    selectAnswersFrom,
    selectArticleFormQuestions,
    selectArticleWorkMode,
    selectCatalog,
    selectCurrentArticle,
    selectCurrentAnswerId,
    selectCurrentBranch,
    selectEditedArticleExtId,
    selectEditedArticleSurvey,
    selectFavoriteArticlesBlock,
    selectSharedTypeState
} from "../selectors"
import { getResultsForPage, toArticleQuestions } from "../../../utility/knowledgeBase/sampleQuestions"
import { ItemId, TreeItem } from "@atlaskit/tree/types"
import { TreeData } from "@atlaskit/tree"
import { convertCategoryToTreeItem, treeItemToMoveCategoryDTO } from "../../../utility/knowledgeBase/categoriesTree"
import { ArticleType } from "../../../models/articleType"
import { toIntentRecordType } from "../../../utility/knowledgeBase/articleTypes"
import { selectCurrentUserLogin } from "../../users/selectors"
import { selectCurrentProjectId } from "../../projects/selectors"
import { ArticleWorkMode } from "../../../models/knowledgeBase/workMode"
import { isDraft, isPublished } from "../../../utility/knowledgeBase/articleStatus"
import { isModifyPermitted } from "../../../utility/knowledgeBase/useModifyPermitted"
import { getArticleTypesBase, getCategoriesBase, getRelatedCategoriesBase, searchArticleBase } from "./root"
import { SharedActualEntityRequest } from "../../../models/sharing"
import axios from "axios"
import { SystemError } from "../../../core/error"
import { handleAddArticleMarkedQuestions, handleSetArticleMarkedQuestionsCount, offCompareMode } from "../actions"
import { ArticleCompareMode, ArticleCompareUpdateState, ComparisonType } from "../../../models/articleCompare"
import compareArticleProcessor from "../../../utility/articleCompare/compareArticleProcessor"
import { saveNotificationError } from "../../notifications/thunks"
import { ArticlePublishInfo } from "../../../models/knowledgeBase/knowledgeBasePublish"
import { compareParamsProcessor } from "../../../utility/articleCompare/compareArticleParamsProcessor"
import { isErrorKeyEquals } from "../../../utility/common/isErrorKeyEquals"

const EX_EXT_ID_EXISTS = "article-with-external-id-already-exists"

export const getCatalogs =
    (projectId: string, catch403Exception = true): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(actions.getCatalogsProcess())
        try {
            const { Catalogs } = await knowledgeBaseController.getCatalogs(projectId)

            dispatch(actions.getCatalogsSuccess(Catalogs))
        } catch (e: unknown) {
            const args: Parameters<typeof handleHttpException> = [
                e,
                constants.GET_CATALOGS_FAILED_MESSAGE,
                (err: SystemError) => actions.getCatalogsFailed(err),
                dispatch
            ]

            if (axios.isAxiosError(e) && e.response?.status === 403) {
                args.push(catch403Exception)
            }

            handleHttpException(...args)
        }
    }

export const getCategories = (
    projectId: string,
    parentCategoryId: ItemId,
    catalogId: ItemId
): ActionResult<Promise<void>> => {
    const func = () => knowledgeBaseController.getCategories(projectId, catalogId as string, parentCategoryId as string)
    return getCategoriesBase(parentCategoryId, catalogId, func)
}

export const getRelatedCategories = (
    projectId: string,
    categoryId?: string,
    categoryCode?: string,
    catalogCode?: string
): ActionResult<Promise<void>> => {
    const func = () => {
        const request: GetRelatedCategoriesRequest = {
            Id: categoryId,
            CatalogCode: catalogCode,
            CategoryCode: categoryCode
        }
        return knowledgeBaseController.getRelatedCategories(projectId, request)
    }
    return getRelatedCategoriesBase(func)
}

export const createCategory =
    (
        projectId: string,
        parentItem: TreeItem,
        url: string,
        nextItem?: TreeItem,
        skipUnsavedPrompt = false
    ): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        dispatch(actions.createCategoryProcess())
        try {
            const {
                id,
                data: { catalogCode, permissions }
            } = parentItem

            const request: CreateCategoryRequest = {
                ParentId: id as string,
                Permissions: permissions,
                PermissionsWithNested: true,
                NextItem: nextItem ? treeItemToMoveCategoryDTO(nextItem) : undefined
            }

            const { CategoryId, Article } = await knowledgeBaseController.createCategory(
                projectId,
                catalogCode,
                request
            )
            dispatch(actions.createCategorySuccess(Article.SymbolCode))

            const state = getState()
            const login = state.users.currentUser.data?.Login
            const roleId = await usersController.getCurrentUserRoleId()

            dispatch(
                actions.getArticleSuccess({
                    Article,
                    Login: login,
                    RoleId: roleId,
                    ProjectId: projectId,
                    IsLocked: false
                })
            )

            const currentArticle = state.knowledgeBase.currentArticle?.data

            dispatch(
                mutateCurrentBranch({
                    CurrentCatalogCode: catalogCode,
                    CurrentCategoryId: CategoryId
                })
            )

            dispatch(
                push({
                    pathname: buildArticleActionUrl(url, ArticleAction.Edit, Article.SymbolCode),
                    state: skipUnsavedPrompt ? { skipUnsavedPrompt: true } : undefined
                })
            )

            dispatch(getRelatedCategories(projectId, CategoryId, currentArticle?.Article?.SymbolCode, catalogCode))
        } catch (e) {
            handleHttpException(
                e,
                constants.CREATE_CATEGORY_FAILED_MESSAGE,
                err => actions.createCategoryFailed(err),
                dispatch
            )
        }
    }

export const moveCategory =
    (
        projectId: string,
        newTree: TreeData,
        sourceParent: TreeItem,
        destinationParent: TreeItem,
        draggableItem: TreeItem,
        prevItem?: TreeItem,
        nextItem?: TreeItem
    ) =>
    async (dispatch: Dispatch) => {
        try {
            dispatch(actions.moveCategoryProcess())

            const request: MoveCategoryRequest = {
                SourceParent: treeItemToMoveCategoryDTO(sourceParent),
                DestinationParent: treeItemToMoveCategoryDTO(destinationParent),
                DraggableItem: treeItemToMoveCategoryDTO(draggableItem),
                PrevItem: prevItem ? treeItemToMoveCategoryDTO(prevItem) : undefined,
                NextItem: nextItem ? treeItemToMoveCategoryDTO(nextItem) : undefined
            }
            dispatch(actions.updateCategoriesTree(newTree as CategoryTreeData))
            const response = await knowledgeBaseController.moveCategory(projectId, request)
            dispatch(actions.moveCategorySuccess(response))
            const { ParentCategory } = response
            dispatch(
                actions.mutateRelatedCategories({
                    ParentCategory: ParentCategory ? convertCategoryToTreeItem(ParentCategory) : undefined
                })
            )
        } catch (e) {
            handleHttpException(
                e,
                constants.MOVE_CATEGORY_FAILED_MESSAGE,
                err => actions.moveCategoryFailed(err),
                dispatch
            )
        }
    }

export const createCatalog =
    (projectId: string, fields: CatalogFormValues, closeCatalogModal: () => void): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(actions.createCatalogProcess())
        try {
            const request: CreateCatalogRequest = {
                Title: fields.Title,
                Permissions: fields.Permissions,
                PermissionsWithNested: true
            }

            const response = await knowledgeBaseController.createCatalog(projectId, request)

            dispatch(actions.createCatalogSuccess(response.Catalog))
            closeCatalogModal()
        } catch (e) {
            handleHttpException(
                e,
                constants.CREATE_CATALOG_FAILED_MESSAGE,
                err => actions.createCatalogFailed(err),
                dispatch
            )
        }
    }

export const updateCatalog =
    (
        projectId: string,
        catalogCode: string,
        fields: CatalogFormValues,
        closeCatalogModal: () => void,
        pageUrl?: string
    ): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        dispatch(actions.updateCatalogProcess())
        try {
            const WithNested = fields.ChangesInheritance === EArticleEditFormChangesInheritance.INHERIT

            const request: UpdateCatalogRequest = {
                Title: fields.Title,
                Permissions: fields.Permissions,
                WithNested
            }

            const catalog = await knowledgeBaseController.updateCatalog(projectId, catalogCode, request)

            closeCatalogModal()

            dispatch(actions.updateCatalogSuccess(catalog))

            if (!catalog.PermittedAction && pageUrl) {
                const { CurrentCatalogCode } = selectCurrentBranch(getState())

                if (CurrentCatalogCode === catalog.SymbolCode) {
                    dispatch(clearCurrentArticle())
                    dispatch(clearCurrentBranch())
                    dispatch(push(pageUrl))
                }
            }
        } catch (e) {
            handleHttpException(
                e,
                constants.UPDATE_CATALOG_FAILED_MESSAGE,
                err => actions.updateCatalogFailed(err),
                dispatch
            )
        }
    }

export const deleteCatalog =
    (projectId: string, catalogCode: string, closeCatalogModal: () => void): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(actions.deleteCatalogProcess())
        try {
            const symbolCode = await knowledgeBaseController.deleteCatalog(projectId, catalogCode)
            dispatch(actions.deleteCatalogSuccess(symbolCode))
            closeCatalogModal()
        } catch (e) {
            handleHttpException(
                e,
                constants.DELETE_CATALOG_FAILED_MESSAGE,
                err => actions.deleteCatalogFailed(err),
                dispatch
            )
        }
    }

export const getCatalogSharing = (projectId: string, catalogCode: string): ActionResult<Promise<void>> => {
    return async (dispatch: Dispatch) => {
        dispatch(actions.shareCatalogBeforeProcess())
        try {
            const response = await knowledgeBaseController.getCatalogSharing(projectId, catalogCode)

            if (response.SharedUrl) {
                dispatch(actions.shareCatalogSuccess(normalizeArticleSharedUrl(response.SharedUrl)))
            }
        } catch (e) {
            handleHttpException(e, constants.GET_CATALOG_SHARING_FAILED_MESSAGE, actions.shareCatalogFailed, dispatch)
        }
    }
}

export const shareCatalog = (projectId: string, catalogCode: string): ActionResult<Promise<void>> => {
    return async (dispatch: Dispatch) => {
        dispatch(actions.shareCatalogProcess())
        try {
            const response = await knowledgeBaseController.shareCatalog(projectId, catalogCode)

            dispatch(actions.shareCatalogSuccess(normalizeArticleSharedUrl(response.SharedUrl)))
        } catch (e) {
            handleHttpException(e, constants.SHARE_CATALOG_FAILED_MESSAGE, actions.shareCatalogFailed, dispatch)
        }
    }
}

export const removeCatalogSharing = (projectId: string, catalogCode: string): ActionResult<Promise<void>> => {
    return async (dispatch: Dispatch) => {
        dispatch(actions.shareCatalogProcess())
        try {
            await knowledgeBaseController.removeCatalogSharing(projectId, catalogCode)
            dispatch(actions.removeCatalogSharingSuccess())
        } catch (e) {
            handleHttpException(
                e,
                constants.REMOVE_CATALOG_SHARING_FAILED_MESSAGE,
                actions.shareCatalogFailed,
                dispatch
            )
        }
    }
}

export const getArticleCompare =
    (projectId: string, firstArticleId: string, secondArticleId: string): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState()
            const currentArticleData = selectCurrentArticle(state)
            dispatch(getArticleProcess())
            const data = await knowledgeBaseController.getArticleCompare(projectId, {
                FirstArticleId: firstArticleId,
                SecondArticleId: secondArticleId
            })

            const compareResult = compareArticleProcessor(data.Comparison.Answers)
            const paramsCompare = compareParamsProcessor(data.Comparison.Parameters)
            if (currentArticleData && currentArticleData.Article?.Answers && currentArticleData.Article?.Answers[0]) {
                const { questionsPayload, addedCount, removedCount, unmodifiedCount } =
                    data.Comparison.Questions.reduce<{
                        questionsPayload: ArticleQuestion[]
                        addedCount: number
                        removedCount: number
                        unmodifiedCount: number
                    }>(
                        (accum, q) => {
                            if (q.Sentence.ComparisonType === ComparisonType.Added) {
                                accum.addedCount++
                            }
                            if (q.Sentence.ComparisonType === ComparisonType.Removed) {
                                accum.removedCount++
                            }
                            if (q.Sentence.ComparisonType === ComparisonType.Unmodified) {
                                accum.unmodifiedCount++
                            }

                            accum.questionsPayload.push({
                                Sentence: q.Sentence.ReplacedValue ? q.Sentence.ReplacedValue : q.Sentence.Value,
                                ComparisonType: q.Sentence.ComparisonType,
                                Type: ArticleQuestionType.example
                            })
                            return accum
                        },
                        { questionsPayload: [], addedCount: 0, removedCount: 0, unmodifiedCount: 0 }
                    )

                const oldQuestionsCount = unmodifiedCount + removedCount
                const questionsCountAfterCompare = questionsPayload.length - removedCount
                const isNothingToCompare = addedCount === 0 && removedCount === 0
                const articleWithNewText: Article = {
                    ...currentArticleData.Article,
                    Answers: [
                        {
                            Id: currentArticleData.Article.Answers[0].Id,
                            Text: compareResult,
                            Slots: currentArticleData.Article.Answers[0].Slots
                        }
                    ],
                    TitleComparison: data.Comparison.Title,
                    Questions: questionsPayload,
                    QuestionsComparisonResult: {
                        OldCount: oldQuestionsCount,
                        CountAfterCompare: questionsCountAfterCompare,
                        isNothingToCompare: isNothingToCompare
                    },
                    Parameters: paramsCompare
                }

                const updateState: CurrentArticleState = {
                    ...currentArticleData,
                    Article: articleWithNewText
                }

                const payload: ArticleCompareUpdateState = {
                    text: compareResult,
                    Article: updateState
                }

                dispatch(actions.setOldArticleVersion(currentArticleData))
                dispatch(actions.getArticleCompareSuccess(payload))
                dispatch(actions.setArticleCompareMode(ArticleCompareMode.DifferentArticles))
            }
        } catch (e) {
            const error = apiCallExceptionToSystemError(constants.GET_ARTICLE_COMPARE_FAILED_MESSAGE, e)
            saveNotificationError(dispatch, error)
        }
    }

export const getArticle =
    (
        projectId: string,
        login: string,
        articleCode: string,
        cancelIfAlreadyLoaded: boolean,
        onlyPublished = false
    ): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState()
            const currentArticleData = selectCurrentArticle(state)

            if (
                cancelIfAlreadyLoaded &&
                currentArticleData &&
                currentArticleData.Article &&
                articleCode === currentArticleData.Article.SymbolCode
            ) {
                return
            }

            dispatch(getArticleProcess())
            const { Article, Views, Mutations, IsLocked } = await knowledgeBaseController.getArticle(
                projectId,
                articleCode,
                onlyPublished
            )
            const roleId = await usersController.getCurrentUserRoleId()
            dispatch(actions.setArticleHistoryMod(false))
            dispatch(
                actions.getArticleSuccess({
                    Article: Article,
                    Login: login,
                    RoleId: roleId,
                    ProjectId: projectId,
                    IsLocked: IsLocked
                })
            )
            if (Article) {
                dispatch(
                    actions.updateArticleUserViewsSuccess({
                        ArticleCode: Article.SymbolCode,
                        Views: Views
                    })
                )
            }
            if (Mutations) {
                await dispatch(handleCategoriesUpdatedEvent(Mutations, projectId))
            }
        } catch (e) {
            handleHttpException(e, constants.GET_ARTICLE_FAILED_MESSAGE, err => actions.getArticleFailed(err), dispatch)
        }
    }

export const searchArticles = (projectId: string, request: SearchArticlesRequest): ActionResult<Promise<void>> => {
    const func = () =>
        knowledgeBaseController.searchArticles(projectId, {
            ...request,
            EnablePayload: true
        })
    return searchArticleBase(func)
}

const dispatchOptimisticArticleUpdate = (
    dispatch: Dispatch,
    currentArticleData: CurrentArticleState | undefined,
    articleUpdates: Partial<Article>
) => {
    if (currentArticleData?.Article) {
        dispatch(
            actions.getArticleSuccess({
                Article: { ...currentArticleData.Article, ...articleUpdates },
                PermittedAction: currentArticleData.PermittedAction,
                IsLocked: currentArticleData.IsLocked
            })
        )
    }
}

export const updateArticle =
    (
        projectId: string,
        url: string,
        code: string,
        fields: ArticleEditFormValues,
        versionId: string
    ): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState()
        const answersForm = selectAnswersFrom(state)
        const questions = selectArticleFormQuestions(state)
        const currentAnswerId = selectCurrentAnswerId(state)
        const editedExtId = selectEditedArticleExtId(state)
        const editedSurvey = selectEditedArticleSurvey(state)
        dispatch(updateArticleProcess())

        try {
            const tagsTrimmed = fields.Tags.trim().substring(1)
            const tags = tagsTrimmed ? tagsTrimmed.split(/\s+#/) : []
            const mappedQuestions = questions.map(toArticleQuestions)
            const request: UpdateArticleRequest = {
                Title: fields.Title,
                Answers: answersForm.Answers,
                Parameters: fields.Parameters.map(p => ({
                    SlotId: p.SlotId,
                    Value: p.Value
                })),
                Type: fields.Type,
                Tags: tags,
                Id: versionId,
                ExtId: editedExtId,
                Survey: editedSurvey,
                Questions: mappedQuestions,
                Expanded: fields.Expanded,
                PublishInfo: fields.PublishInfo
            }

            const response = await knowledgeBaseController.updateArticle(projectId, code, request)
            const currentArticleData = selectCurrentArticle(getState())

            batch(() => {
                dispatch(
                    push({
                        pathname: currentAnswerId
                            ? `${buildArticleActionUrl(url, ArticleAction.View, code)}/${currentAnswerId}`
                            : buildArticleActionUrl(url, ArticleAction.View, code),
                        state: { skipUnsavedPrompt: true }
                    })
                )

                dispatchOptimisticArticleUpdate(dispatch, currentArticleData, {
                    Title: fields.Title,
                    Answers: answersForm.Answers,
                    Type: fields.Type,
                    Tags: tags,
                    Parameters: request.Parameters,
                    Questions: mappedQuestions,
                    ExtId: editedExtId,
                    Survey: editedSurvey,
                    Expanded: fields.Expanded,
                    PublishInfo: fields.PublishInfo
                })

                if (currentAnswerId) {
                    dispatch(actions.updateCurrentAnswerById(currentAnswerId))
                }

                dispatch(actions.updateArticleSuccess(response))
            })
        } catch (e) {
            const error = apiCallExceptionToSystemError(constants.UPDATE_ARTICLE_FAILED_MESSAGE, e)
            if (isErrorKeyEquals(e, EX_EXT_ID_EXISTS)) {
                saveNotificationError(dispatch, error)
                dispatch(actions.updateArticleSuccess(code))
            } else {
                handleHttpException(
                    e,
                    constants.UPDATE_ARTICLE_FAILED_MESSAGE,
                    err => actions.updateArticleFailed(err),
                    dispatch
                )
            }
        }
    }

export const updateArticlePermissions =
    (
        projectId: string,
        url: string,
        code: string,
        permissions: KnowledgeBasePermission[],
        changesInheritance: EArticleEditFormChangesInheritance,
        versionId: string,
        hidePopover: () => void
    ): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(updateArticleProcess())
        try {
            const WithNested = changesInheritance === EArticleEditFormChangesInheritance.INHERIT

            const request: UpdateActualArticlePermissionsRequest = {
                Permissions: permissions,
                Id: versionId,
                WithNested
            }

            const response = await knowledgeBaseController.updateArticlePermissions(projectId, code, request)
            dispatch(actions.updateArticleSuccess(response))
            hidePopover()
        } catch (e) {
            handleHttpException(
                e,
                constants.UPDATE_ARTICLE_FAILED_MESSAGE,
                err => actions.updateArticleFailed(err),
                dispatch
            )
        }
    }

const changeArticleStatus =
    (errorMessage: string, waitResponse: Promise<string>): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(updateArticleProcess())
        try {
            const response = await waitResponse
            dispatch(actions.updateArticleSuccess(response))
        } catch (e) {
            handleHttpException(e, errorMessage, err => actions.updateArticleFailed(err), dispatch)
        }
    }

export const publishArticle = (
    projectId: string,
    url: string,
    code: string,
    versionId: string,
    publishInfo: ArticlePublishInfo
): ActionResult<Promise<void>> => {
    const errorMessage = constants.PUBLISH_ARTICLE_FAILED_MESSAGE
    const request: PublishArticleRequest = {
        Id: versionId,
        PublishInfo: publishInfo
    }

    const waitResponse = knowledgeBaseController.publishArticle(projectId, code, request)
    return changeArticleStatus(errorMessage, waitResponse)
}

export const removeArticle = (
    projectId: string,
    url: string,
    code: string,
    versionId: string
): ActionResult<Promise<void>> => {
    const errorMessage = constants.REMOVE_ARTICLE_FAILED_MESSAGE
    const waitResponse = knowledgeBaseController.removeArticle(projectId, code, { Id: versionId })
    return changeArticleStatus(errorMessage, waitResponse)
}

export const setUnpublishedStatusForArticle = (
    projectId: string,
    url: string,
    code: string,
    versionId: string
): ActionResult<Promise<void>> => {
    const errorMessage = constants.SET_UNPUBLISHED_STATUS_FOR_ARTICLE_FAILED_MESSAGE
    const waitResponse = knowledgeBaseController.setUnpublishedStatusFortArticle(projectId, code, { Id: versionId })
    return changeArticleStatus(errorMessage, waitResponse)
}

export const sendArticleForApproval =
    (projectId: string, url: string, code: string, versionId: string): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(updateArticleProcess())
        try {
            const article = await knowledgeBaseController.sendArticleForApproval(projectId, code, { Id: versionId })
            dispatch(actions.updateArticleSuccess(article))
        } catch (e) {
            handleHttpException(
                e,
                constants.SEND_ARTICLE_FOR_APPROVAL_FAILED_MESSAGE,
                err => actions.updateArticleFailed(err),
                dispatch
            )
        }
    }

export const shareArticle = (
    projectId: string,
    articleCode: string,
    articleVersionId: string,
    login: string
): ActionResult<Promise<void>> => {
    return async (dispatch: Dispatch) => {
        dispatch(actions.shareArticleProcess())
        try {
            const roleId = await usersController.getCurrentUserRoleId()
            const request: SharedActualEntityRequest = {
                Id: articleVersionId,
                Login: login,
                RoleId: roleId
            }
            const response = await knowledgeBaseController.shareArticle(projectId, articleCode, request)

            dispatch(actions.shareArticleSuccess(normalizeArticleSharedUrl(response.SharedUrl)))
        } catch (e) {
            handleHttpException(e, constants.SHARE_ARTICLE_FAILED_MESSAGE, actions.shareArticleFailed, dispatch)
        }
    }
}

export const getArticleSharing = (projectId: string, articleCode: string): ActionResult<Promise<void>> => {
    return async (dispatch: Dispatch) => {
        dispatch(actions.shareArticleBeforeProcess())
        try {
            const response = await knowledgeBaseController.getArticleSharing(projectId, articleCode)

            if (response.SharedUrl) {
                dispatch(actions.shareArticleSuccess(normalizeArticleSharedUrl(response.SharedUrl)))
            }
        } catch (e) {
            handleHttpException(e, constants.GET_ARTICLE_SHARING_FAILED_MESSAGE, actions.shareArticleFailed, dispatch)
        }
    }
}

export const removeArticleSharing = (
    projectId: string,
    articleCode: string,
    articleVersionId: string,
    login: string
): ActionResult<Promise<void>> => {
    return async (dispatch: Dispatch) => {
        dispatch(actions.removeArticleSharingProcess())
        try {
            const roleId = await usersController.getCurrentUserRoleId()
            const request: SharedActualEntityRequest = {
                Id: articleVersionId,
                Login: login,
                RoleId: roleId
            }
            await knowledgeBaseController.removeArticleSharing(projectId, articleCode, request)
            dispatch(actions.removeArticleSharingSuccess())
        } catch (e) {
            handleHttpException(
                e,
                constants.REMOVE_ARTICLE_SHARING_FAILED_MESSAGE,
                actions.removeArticleSharingFailed,
                dispatch
            )
        }
    }
}

export function getArticleTypes(projectId: string) {
    const func = () => knowledgeBaseController.getArticleTypes(projectId)
    return getArticleTypesBase(func)
}

export function createArticleType(projectId: string, createdArticleType: ArticleType, callback: () => void) {
    return async (dispatch: Dispatch) => {
        try {
            dispatch(actions.createArticleTypeProcess())
            await knowledgeBaseController.createArticleType(toIntentRecordType(createdArticleType, projectId))
            dispatch(actions.createArticleTypeSuccess(createdArticleType))
            callback()
        } catch (e) {
            handleHttpException(
                e,
                constants.CREATE_ARTICLE_TYPE_FAILED_MESSAGE,
                err => actions.createArticleTypeFailed(err),
                dispatch
            )
        }
    }
}

export function updateArticleType(projectId: string, updatedArticleType: ArticleType, callback: () => void) {
    return async (dispatch: Dispatch) => {
        try {
            dispatch(actions.updateArticleTypeProcess())
            await knowledgeBaseController.updateArticleType(toIntentRecordType(updatedArticleType, projectId))
            dispatch(actions.updateArticleTypeSuccess(updatedArticleType))
            callback()
        } catch (e) {
            handleHttpException(
                e,
                constants.UPDATE_ARTICLE_TYPE_FAILED_MESSAGE,
                err => actions.updateArticleTypeFailed(err),
                dispatch
            )
        }
    }
}

export function deleteArticleType(articleTypeId: string) {
    return async (dispatch: Dispatch) => {
        try {
            dispatch(actions.deleteArticleTypeProcess())
            await knowledgeBaseController.deleteArticleType(articleTypeId)
            dispatch(actions.deleteArticleTypeSuccess(articleTypeId))
        } catch (e) {
            handleHttpException(
                e,
                constants.DELETE_ARTICLE_TYPE_FAILED_MESSAGE,
                err => actions.deleteArticleTypeFailed(err),
                dispatch
            )
        }
    }
}

export const updateScenario =
    (
        projectId: string,
        url: string,
        code: string,
        fields: ArticleEditFormValues,
        scenario: Scenario,
        versionId: string
    ): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        dispatch(updateArticleProcess())

        const state = getState()
        const questions = selectArticleFormQuestions(state)
        const editedExtId = selectEditedArticleExtId(state)
        const editedSurvey = selectEditedArticleSurvey(state)

        try {
            const tagsTrimmed = fields && fields.Tags.trim().substring(1)
            const tags = tagsTrimmed ? tagsTrimmed.split(/\s+#/) : []
            const mappedQuestions = questions.map(toArticleQuestions)
            const request: UpdateScenarioRequest = {
                Title: fields && fields.Title,
                Tags: tags,
                Type: fields && fields.Type,
                Scenario: scenario,
                Id: versionId,
                ExtId: editedExtId,
                Survey: editedSurvey,
                Questions: mappedQuestions,
                PublishInfo: fields.PublishInfo
            }
            const response = await knowledgeBaseController.updateScenario(projectId, code, request)
            const currentArticleData = selectCurrentArticle(getState())

            batch(() => {
                dispatch(
                    push({
                        pathname: buildArticleActionUrl(url, ArticleAction.View, code),
                        state: { skipUnsavedPrompt: true }
                    })
                )

                dispatchOptimisticArticleUpdate(dispatch, currentArticleData, {
                    Title: fields.Title,
                    Type: fields.Type,
                    Tags: tags,
                    Scenario: scenario,
                    Questions: mappedQuestions,
                    ExtId: editedExtId,
                    Survey: editedSurvey,
                    PublishInfo: fields.PublishInfo
                })

                dispatch(actions.updateArticleSuccess(response))
            })
        } catch (e) {
            handleHttpException(
                e,
                constants.UPDATE_ARTICLE_FAILED_MESSAGE,
                err => actions.updateArticleFailed(err),
                dispatch
            )
        }
    }

export const refreshArticle =
    (projectId: string, url: string, code: string): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(updateArticleProcess())
        try {
            const response = await knowledgeBaseController.refreshArticle(projectId, code)
            dispatch(actions.updateArticleSuccess(response))
        } catch (e) {
            handleHttpException(
                e,
                constants.UPDATE_ARTICLE_FAILED_MESSAGE,
                err => actions.updateArticleFailed(err),
                dispatch
            )
        }
    }

export const confirmArticleRead =
    (projectId: string, url: string, code: string, notificationId: string): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        dispatch(actions.updateArticleUserViewsProcess())
        try {
            const articleUserViews = await knowledgeBaseController.confirmArticleRead(projectId, code)
            await notificationController.approve(notificationId)
            if (articleUserViews) {
                dispatch(
                    actions.updateArticleUserViewsSuccess({
                        ArticleCode: code,
                        Views: articleUserViews
                    })
                )
                dispatch(push(buildArticleActionUrl(url, ArticleAction.View, code)))
            }
        } catch (e) {
            handleHttpException(
                e,
                constants.SAVE_ARTICLE_READ_CONFIRMATION_FAILED_MESSAGE,
                err => actions.updateArticleUserViewsFailed(err),
                dispatch
            )
        }
    }

export const handleArticleUpdatedEvent =
    (pageUrl: string, articleCode: string, updatedArticle?: Article): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState()
        const shareType = selectSharedTypeState(state)
        const skipUnsavedPrompt = state.knowledgeBase.updateArticle.inProcess

        // if the article is shared, we need to update the shared article and interrupt further logic
        if (shareType.data) {
            dispatch(actions.getSharedArticleSuccess({ Article: updatedArticle }))
            dispatch(push(buildSharedArticleActionUrl(pageUrl, articleCode)))
            return
        }

        const login = selectCurrentUserLogin(state)
        const roleId = await usersController.getCurrentUserRoleId()
        const projectId = selectCurrentProjectId(state)

        if (!updatedArticle) {
            dispatch(
                actions.getArticleSuccess({
                    Article: undefined,
                    PermittedAction: undefined,
                    Login: login,
                    RoleId: roleId,
                    ProjectId: projectId
                })
            )
            return
        }

        const { Article } = selectCurrentArticle(state) || {
            Article: undefined
        }

        // we may not update the article (don't show the version reload screen) if the current version is published,
        // and the received one is a draft with view permitted action
        const { Permissions, Status } = updatedArticle
        const updatedArticlePermittedAction =
            projectId && login ? getArticlePermittedAction(Permissions, Status, projectId, login, roleId) : undefined

        if (
            Article &&
            isPublished(Article.Status) &&
            isDraft(updatedArticle.Status) &&
            !isModifyPermitted(updatedArticlePermittedAction)
        ) {
            return
        }

        dispatch(
            actions.getArticleSuccess({
                Article: updatedArticle,
                PermittedAction: updatedArticlePermittedAction,
                Login: login,
                RoleId: roleId,
                ProjectId: projectId
            })
        )
        dispatch(
            push({
                pathname: buildArticleActionUrl(pageUrl, ArticleAction.View, updatedArticle.SymbolCode),
                state: skipUnsavedPrompt ? { skipUnsavedPrompt: true } : undefined
            })
        )
    }

export const handleArticleUpdatedLockStateEvent =
    (pageUrl: string, articleCode: string, isLocked: boolean): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState()

        const { Article } = selectCurrentArticle(state) || { Article: undefined }
        if (Article?.SymbolCode !== articleCode) {
            return
        }

        dispatch(setArticleLockStatus(isLocked))
        dispatch(push(buildArticleActionUrl(pageUrl, ArticleAction.View, articleCode)))
    }

export const handleCategoryAddedEvent =
    (event: CategoryAddedEvent): ActionResult<void> =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const { Category, ParentItemId } = event
        const state = getState()

        const catalog = state.knowledgeBase.catalogsMap[Category.CatalogCode]
        if (!catalog) {
            return
        }

        const parentItem = catalog.items[ParentItemId]
        if (!parentItem) {
            return
        }

        dispatch(actions.addCategory({ ParentItemId, Category }))
    }

export const handleCategoriesUpdatedByChildEvent =
    (event: RemoveCategoryFromCatalogEvent): ActionResult<void> =>
    (dispatch: Dispatch) => {
        const { CategoryId, CatalogCode, ParentId } = event
        if (!CatalogCode || !ParentId) return

        dispatch(
            actions.removeCategoryFromCatalogSuccess({
                CatalogId: CatalogCode,
                ParentCategoryId: ParentId,
                CategoryId: CategoryId
            })
        )
    }

export const handleCategoriesUpdatedEvent =
    (data: CategoryMutation[], dialogProjectId?: string): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState()
        const projectId = dialogProjectId || selectCurrentProjectId(state)
        const login = selectCurrentUserLogin(state)
        const roleId = await usersController.getCurrentUserRoleId()
        const mutations =
            projectId && login
                ? data.map(m => {
                      if (m.Permissions && m.Status) {
                          m.PermittedAction = getArticlePermittedAction(
                              m.Permissions || [],
                              m.Status,
                              projectId,
                              login,
                              roleId
                          )
                      }
                      return m
                  })
                : data
        dispatch(actions.updateCategories(mutations))
    }
export const handleFavoriteRemoveEvent =
    (data: CategoryMutation[]) => async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState()
        const projectId = selectCurrentProjectId(state) || ""
        const login = selectCurrentUserLogin(state) || ""
        const roleId = await usersController.getCurrentUserRoleId()

        data.forEach(mutation => {
            const symbolCode = mutation.SymbolCode

            const permittedAction = getArticlePermittedAction(
                mutation.Permissions ?? [],
                mutation.Status ?? ArticleStatusType.Draft,
                projectId,
                login,
                roleId
            )

            const { data } = selectFavoriteArticlesBlock(state)
            if (data) {
                const favorite = data.items
                if (!permittedAction && favorite[symbolCode]) {
                    const newTree = data
                    delete newTree.items[symbolCode]
                    dispatch(updateArticleFavoriteBlockTree(newTree))
                }
            }
        })
    }

export const reloadCatalogs = async (dispatch: Dispatch, getState: () => RootState) => {
    const projectId = selectCurrentProjectId(getState())
    if (projectId) {
        await dispatch(getCatalogs(projectId))
    }
}

export const handleCatalogUpdatedEvent =
    (data: Catalog): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState()
        const { ProjectId: projectId, SymbolCode: catalogId, Permissions: catalogPermissions } = data
        const login = selectCurrentUserLogin(state)
        const currentProjectId = selectCurrentProjectId(state)
        const roleId = await usersController.getCurrentUserRoleId()

        if (!login || !roleId || !projectId || !currentProjectId) return

        const tree = selectCatalog(state, catalogId)
        const catalogPermissionsPrevState = tree?.items?.[catalogId]?.data?.permissions

        const prevAccess = hasCatalogAccess(currentProjectId, login, roleId, catalogPermissionsPrevState)
        const currentAccess = hasCatalogAccess(currentProjectId, login, roleId, catalogPermissions)

        if (prevAccess !== currentAccess) {
            if (currentAccess) {
                await dispatch(getCatalogs(projectId))
            } else {
                dispatch(actions.deleteCatalogSuccess(catalogId))
            }
        }
    }

export const reloadCurrentArticle = async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState()
    const { CurrentCatalogCode, CurrentCategoryId } = selectCurrentBranch(state)
    const currentArticleData = selectCurrentArticle(state)
    const articleWorkMode = selectArticleWorkMode(state)
    const login = selectCurrentUserLogin(state)
    const projectId = selectCurrentProjectId(state)

    if (!login || !projectId || !currentArticleData || !currentArticleData.Article) {
        return
    }

    const {
        Article: { SymbolCode }
    } = currentArticleData

    if (articleWorkMode === ArticleWorkMode.View) {
        await dispatch(getArticle(projectId, login, SymbolCode, false))
    }
    await dispatch(getRelatedCategories(projectId, CurrentCategoryId, undefined, CurrentCatalogCode))
}

export const getDefaultArticle = (projectId: string) => {
    return async (dispatch: Dispatch) => {
        try {
            dispatch(actions.getDefaultArticleProcess())
            const { Article } = await knowledgeBaseController.getDefaultArticleByRole(projectId)
            dispatch(actions.getDefaultArticleSuccess(Article ? Article.SymbolCode : false))
        } catch (e) {
            handleHttpException(
                e,
                constants.GET_DEFAULT_ARTICLE_FAILED_MESSAGE,
                err => actions.getDefaultArticleFailed(err),
                dispatch
            )
        }
    }
}

export const setOperatorQuestions = async (
    articleSymbolCode: string,
    start: number,
    length: number,
    dispatch: Dispatch
) => {
    if (length <= 0) {
        handleAddArticleMarkedQuestions([], dispatch)
        return
    }

    const result = await getResultsForPage(articleSymbolCode, start, dispatch, length)
    if (result) {
        const { data } = result
        const markedQuestions = data.map(item => ({ ...item, type: QuestionType.Operator }))

        handleAddArticleMarkedQuestions(markedQuestions, dispatch)
    } else {
        handleAddArticleMarkedQuestions([], dispatch)
    }
}

export const setOperatorQuestionsCount = async (articleSymbolCode: string, count: number, dispatch: Dispatch) => {
    const result = await getResultsForPage(articleSymbolCode, count, dispatch)
    if (result) {
        const { recordsTotal } = result
        handleSetArticleMarkedQuestionsCount(recordsTotal, dispatch)
    } else {
        handleSetArticleMarkedQuestionsCount(0, dispatch)
    }
}

export const setFavoriteArticle =
    (
        projectId: string,
        articleCode: string,
        articleTitle: string,
        isScenario: boolean,
        isFavorite: boolean,
        categoryCode: string
    ): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        try {
            const request: FavoriteArticlesBlockRequest = {
                ArticleTitle: articleTitle,
                IsFavorite: isFavorite,
                CategoryCode: categoryCode,
                IsScenario: isScenario
            }

            dispatch(actions.setArticleFavoriteProcess())

            const { FavoriteArticlesBlock } = await knowledgeBaseController.setArticleFavorite(
                projectId,
                articleCode,
                request
            )

            dispatch(actions.setArticleFavoriteSuccess(isFavorite))
            dispatch(actions.expandArticleFavoriteBlock(FavoriteArticlesBlock.IsExpand))
            dispatch(actions.getArticleFavoriteBlockSuccess(FavoriteArticlesBlock))
        } catch (e) {
            handleHttpException(
                e,
                constants.SET_FAVORITE_ARTICLE_FAILED_MESSAGE,
                err => actions.setArticleFavoriteFailed(err),
                dispatch
            )
        }
    }

export const getFavoriteArticlesBlock =
    (projectId: string): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        try {
            dispatch(actions.getArticleFavoriteBlockProcess())

            const emptyFavoriteBlock: FavoriteArticlesBlock = {
                Id: "",
                Login: "",
                ArticlesInfo: [],
                IsExpand: false,
                CustomerId: ""
            }

            const { FavoriteArticlesBlock = emptyFavoriteBlock, Categories } =
                await knowledgeBaseController.getFavoriteArticlesBlock(projectId)

            dispatch(actions.getArticleFavoriteBlockSuccess(FavoriteArticlesBlock))
            dispatch(actions.expandArticleFavoriteBlock(FavoriteArticlesBlock.IsExpand))
            dispatch(actions.setCategories(Categories))
        } catch (e) {
            handleHttpException(
                e,
                constants.GET_FAVORITE_ARTICLES_BLOCK_FAILED_MESSAGE,
                err => actions.getArticleFavoriteBlockFailed(err),
                dispatch
            )
        }
    }

export const expandFavoriteArticlesBlock =
    (projectId: string, isExpanded: boolean): ActionResult<Promise<void>> =>
    async (dispatch: Dispatch) => {
        try {
            const request: FavoriteArticlesBlockExpandRequest = {
                IsExpand: !isExpanded
            }

            dispatch(actions.expandArticleFavoriteBlock(!isExpanded))

            await knowledgeBaseController.expandFavoriteBlock(projectId, request)
        } catch (e) {
            handleHttpExceptionWithoutAction(e, constants.EXPAND_FAVORITE_ARTICLES_BLOCK_FAILED_MESSAGE, dispatch)
        }
    }

export const moveArticleInFavoriteBlock =
    (projectId: string, newArticleInfo: FavoriteArticleInfo[]) => async (dispatch: Dispatch) => {
        try {
            const request: FavoriteArticlesBlockMoveRequest = {
                ArticlesInfo: newArticleInfo
            }

            dispatch(actions.moveFavoriteArticleProcess())

            const response = await knowledgeBaseController.moveArticleOfFavoriteBlock(projectId, request)
            const { FavoriteArticlesBlock } = response

            dispatch(actions.moveFavoriteArticleSuccess())
            dispatch(actions.getArticleFavoriteBlockSuccess(FavoriteArticlesBlock))
        } catch (e) {
            handleHttpException(
                e,
                constants.MOVE_FAVORITE_ARTICLE_FAILED_MESSAGE,
                err => actions.moveFavoriteArticleFailed(err),
                dispatch
            )
        }
    }

export const getArticlePreviousVersion =
    (projectId: string, articleId: string, isLastVersion: boolean | undefined) =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState()
            const currentArticleData = selectCurrentArticle(state)
            const { Article } = await knowledgeBaseController.getArticlePreviousVersion(projectId, articleId)
            const payload: CurrentArticleState = {
                ...currentArticleData,
                Article: Article
            }

            if (state.knowledgeBase.articleCompareMode !== ArticleCompareMode.Default) {
                dispatch(offCompareMode())
            }

            dispatch(actions.setNewArticleFromState(payload))
            dispatch(actions.setArticleHistoryMod(!isLastVersion))
        } catch (e) {
            const error = apiCallExceptionToSystemError(constants.GET_ARTICLE_PREVIOS_VERSION_FAILED_MESSAGE, e)
            saveNotificationError(dispatch, error)
        }
    }

export const createDraftFromArticle =
    (projectId: string, articleCode: string, articleId: string) =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState()
            const currentArticleData = selectCurrentArticle(state)
            dispatch(updateArticleProcess())
            const { Article } = await knowledgeBaseController.createDraftFrom(projectId, articleCode, articleId)
            const payload: CurrentArticleState = {
                ...currentArticleData,
                Article: Article
            }
            if (payload.Article) {
                dispatch(actions.setNewArticleFromState(payload))
                dispatch(actions.updateArticleSuccess(payload.Article.SymbolCode))
            }
        } catch (e) {
            const error = apiCallExceptionToSystemError(constants.GET_ARTICLE_PREVIOS_VERSION_FAILED_MESSAGE, e)
            saveNotificationError(dispatch, error)
        }
    }

export const toMarkedMessageHash = (messages: MarkedMessage[]) => {
    return messages.reduce<MarkedMessageHash>((acc, msg) => {
        acc[msg.id] = { ...msg, isSelected: false }
        return acc
    }, {})
}

export const getUnmarkedMessages =
    (text: string, count: number) => async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState()
            dispatch(getUnmarkedMessagesProcess())
            const articleSampleQuestions = selectArticleFormQuestions(state).map(message => message.text)
            const examples = text ? [text] : articleSampleQuestions
            const request: GetUnmarkedMessageRequest = {
                excludedMessages: articleSampleQuestions,
                examples: examples,
                count: count
            }
            const messages = await knowledgeBaseController.getUnmarkedMessages(request)
            const parsedMessages = toMarkedMessageHash(messages)
            dispatch(getUnmarkedMessagesSuccess(parsedMessages))
        } catch (e) {
            handleHttpException(
                e,
                constants.GET_UNMARKED_MESSAGES_FAILED_MESSAGE,
                err => getUnmarkedMessagesFailed(err),
                dispatch
            )
        }
    }

export const updateUnmarkedMessages =
    (text: string, count: number, previousMessage: string) => async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState()
            const articleSampleQuestions = selectArticleFormQuestions(state).map(message => message.text)
            const examples = text ? [text] : articleSampleQuestions
            const request: GetUnmarkedMessageRequest = {
                excludedMessages: articleSampleQuestions,
                examples: examples,
                previousMessage: previousMessage,
                count: count
            }
            const messages = await knowledgeBaseController.getUnmarkedMessages(request)
            const parsedMessages = toMarkedMessageHash(messages)
            dispatch(updateUnmarkedMessageSuccess(parsedMessages))
        } catch (e) {
            handleHttpException(
                e,
                constants.GET_UNMARKED_MESSAGES_FAILED_MESSAGE,
                err => getUnmarkedMessagesFailed(err),
                dispatch
            )
        }
    }
